import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {ActivatedRouteSnapshot, RouterModule, Routes} from '@angular/router';

import {NgApexchartsModule} from 'ng-apexcharts';
import {NgxCaptchaModule} from 'ngx-captcha';
import {APP_SETTINGS, USER_DETAILS} from 'telemetry-library';
import {AccountModule} from '../account/account.module';
import {ContactUnsubscribeComponent} from '../account/contact-unsubscribe/contact-unsubscribe.component';
import {SignRequestComponent} from '../account/sign-request/sign-request.component';
import {AlreadyLoggedInGuard} from '../guards/already-logged-in.guard';
import {AnonymousPageGuard} from '../guards/anonymous-page.guard';
import {AuthorizedGpGuard} from '../guards/authorized-gp.guard';
import {AuthorizedLpGuard} from '../guards/authorized-lp.guard';
import {LpOnboardingPassedGuard} from '../guards/lp-onboarding-passed.guard';
import {LpOnboardingRequiredGuard} from '../guards/lp-onboarding-required.guard';
import {LpViewAsInvestorGuard} from '../guards/lp-view-as-investor-guard.service';
import {MaintenanceGuard} from '../guards/maintenance.guard';
import {PendingChangesGuard} from '../guards/pendingChanges.guard';
import {SubdomainLpGuard} from '../guards/subdomain-lp.guard';
import {VerifyEmailGuard} from '../guards/verify-email.guard';
import {AppSettingsService} from '../services/app-settings.service';
import {LpAssetDataService} from '../services/lp/lp-asset-data.service';
import {LpBankAccountDataService} from '../services/lp/lp-bank-account-data.service';
import {LpFundDataService} from '../services/lp/lp-fund-data.service';
import {LpHoldingDataService} from '../services/lp/lp-holding-data.service';
import {LpUnitBankAccountDataService} from '../services/lp/lp-unit-bank-account-data.service';
import {LpUnitBankApplicationDataService} from '../services/lp/lp-unit-bank-application-data.service';
import {LpUserDataService} from '../services/lp/lp-user-data.service';
import {AssetReportDataService} from '../services/shared/asset-report-data.service';
import {FundReportDataService} from '../services/shared/fund-report-data.service';
import {UnitBankAccountDataService} from '../services/shared/unit-bank-account-data.service';
import {UnitBankApplicationDataService} from '../services/shared/unit-bank-application-data.service';
import {UnitCardDataService} from '../services/shared/unit-card-data.service';
import {UserService} from '../services/shared/user.service';
import {CovercySharedComponentsModule} from '../shared/CovercySharedComponents.module';
import {FilePreviewPageComponent} from '../shared/components/file-preview-page/file-preview-page.component';
import {HelpSourceType} from '../shared/enums/HelpSourceType.enum';
import {HoldingDiscriminator} from '../shared/enums/HoldingDiscriminator.enum';
import {MaterialModule} from '../shared/material.module';
import {SharedModule} from '../shared/shared.module';
import {LoginLpComponent} from './account/login/login-lp.component';
import {RegisterLpComponent} from './account/register/register-lp.component';
import {AddYourAssetCardComponent} from './assets/add-your-asset-card/add-your-asset-card.component';
import {GpLeadDialogComponent} from './assets/add-your-asset-card/gp-lead-dialog/gp-lead-dialog.component';
import {AssetDetailsDialogComponent} from './assets/lp-asset/lp-asset-information-tab/asset-details-section/asset-details-dialog/asset-details-dialog.component';
import {AssetDetailsSectionComponent} from './assets/lp-asset/lp-asset-information-tab/asset-details-section/asset-details-section.component';
import {AssetSpendToDateSectionComponent} from './assets/lp-asset/lp-asset-information-tab/asset-spend-to-date-section/asset-spend-to-date-section.component';
import {LpAssetTransactionsTabComponent} from './assets/lp-asset/lp-asset-transactions-tab/lp-asset-transactions-tab.component';
import {LpAssetComponent} from './assets/lp-asset/lp-asset.component';
import {LpAssetsComponent} from './assets/lp-assets.component';
import {LpFundTransactionsTabComponent} from './funds/lp-fund/lp-fund-transactions-tab/lp-fund-transactions-tab.component';
import {LpFundComponent} from './funds/lp-fund/lp-fund.component';
import {LpFundsComponent} from './funds/lp-funds.component';
import {InvestingEntityShareComponent} from './investing-entity-share/investing-entity-share.component';
import {InvestingEntityShareService} from './investing-entity-share/services/investing-entity-share.service';
import {
  LpCapitalCallDetailsLineItemComponent
} from './lp-holdings/lp-holding-capital-call-tab/lp-capital-call-details/lp-capital-call-details-line-item/lp-capital-call-details-line-item.component';
import {LpCapitalCallDetailsComponent} from './lp-holdings/lp-holding-capital-call-tab/lp-capital-call-details/lp-capital-call-details.component';
import {LpHoldingCapitalCallTabComponent} from './lp-holdings/lp-holding-capital-call-tab/lp-holding-capital-call-tab.component';
import {LpHoldingCardComponent} from './lp-holdings/lp-holding-card/lp-holding-card.component';
import {HoldingCapitalInformationComponent} from './lp-holdings/lp-holding-information-tab/holding-capital-information/holding-capital-information.component';
import {HoldingInvestmentPerformanceComponent} from './lp-holdings/lp-holding-information-tab/holding-investment-performance/holding-investment-performance.component';
import {HoldingMyPositionSectionComponent} from './lp-holdings/lp-holding-information-tab/holding-my-position-section/holding-my-position-section.component';
import {HoldingProjectInformationComponent} from './lp-holdings/lp-holding-information-tab/holding-project-information/holding-project-information.component';
import {HoldingReportComponent} from './lp-holdings/lp-holding-information-tab/holding-reports/holding-report/holding-report.component';
import {HoldingReportsComponent} from './lp-holdings/lp-holding-information-tab/holding-reports/holding-reports.component';
import {LpHoldingInformationTabComponent} from './lp-holdings/lp-holding-information-tab/lp-holding-information-tab.component';
import {LpHoldingsCardsComponent} from './lp-holdings/lp-holdings-cards/lp-holdings-cards.component';
import {LpHoldingsComponent} from './lp-holdings/lp-holdings.component';
import {CompanyProfileLpComponent} from './lp-profile/company-profile-lp/company-profile-lp.component';
import {LpProfileComponent} from './lp-profile/lp-profile.component';
import {PersonalDetailsLpComponent} from './lp-profile/personal-details-lp/personal-details-lp.component';
import {LpTopNotificationComponent} from './lp-top-notification/lp-top-notification.component';
import {LpComponent} from './lp.component';
import {OnboardingLpOrganizationDetailsComponent} from './onboarding-lp/onboarding-lp-organization-details/onboarding-lp-organization-details.component';
import {OnboardingLpPersonalDetailsComponent} from './onboarding-lp/onboarding-lp-personal-details/onboarding-lp-personal-details.component';
import {OnboardingLpComponent} from './onboarding-lp/onboarding-lp.component';
import {BulletBigValueComponent} from './shared/bullet-big-value/bullet-big-value.component';
import {FileOptionsMenuComponent} from './shared/holdings/documents/lp-documents-tab/file-options-menu/file-options-menu.component';
import {LpDocumentsTabComponent} from './shared/holdings/documents/lp-documents-tab/lp-documents-tab.component';
import {SelectAndAgreeComponent} from './shared/holdings/documents/lp-documents-tab/lp-share-approve/lp-share-approve.component';
import {HoldingPositionChartComponent} from './shared/holdings/holding-cash-flow/holding-cash-flow.component';
import {HoldingGeneralInformationComponent} from './shared/holdings/holding-general-information/holding-general-information.component';
import {LenderInfoDialogComponent} from './shared/holdings/lender-info-dialog/lender-info-dialog.component';
import {LenderJuniorInfoDialogComponent} from './shared/holdings/lender-junior-info-dialog/lender-junior-info-dialog.component';
import {PrivateHoldingNotificationComponent} from './shared/holdings/private-holding-notification/private-holding-notification.component';
import {ReportFaqsDialogComponent} from './shared/holdings/report-faqs-dialog/report-faqs-dialog.component';
import {InfoDialogComponent} from './shared/info-dialog/info-dialog.component';
import {LpHoldingTabsNavComponent} from './shared/lp-holding-tabs-nav/lp-holding-tabs-nav.component';
import {LpLeverageGraphComponent} from './shared/lp-leverage/lp-leverage-graph/lp-leverage-graph.component';
import {LpLeverageTableComponent} from './shared/lp-leverage/lp-leverage-table/lp-leverage-table.component';
import {LpLeverageComponent} from './shared/lp-leverage/lp-leverage.component';
import {LpTransactionDetailsDialogComponent} from './shared/lp-transaction-details-dialog/lp-transaction-details-dialog.component';
import {InvestingEntityChipComponent} from './investing-entity-share/investing-entity-chip.component';
import {InvestingEntityMenuComponent} from './investing-entity-share/investing-entity-menu.component';
import {LpAssetMobileFilterComponent} from './assets/lp-asset/lp-assets-filter/lp-asset-mobile-filter.component';
import {LpAssetFilterComponent} from './assets/lp-asset/lp-assets-filter/lp-asset-filter.component';
import {AccountLpModule} from './account/account-lp.module';

const defaultHoldingTab = 'information';
const holdingPageChildren = (holdingType: 'Fund' | 'Asset'): Routes => [
  {
    path: 'information',
    pathMatch: 'full',
    component: LpHoldingInformationTabComponent,
    data: {preventScroll: true}
  },
  {
    path: 'transactions',
    pathMatch: 'full',
    component: holdingType === 'Asset' ? LpAssetTransactionsTabComponent : LpFundTransactionsTabComponent,
    data: {preventScroll: true}
  },
  {
    path: 'documents',
    pathMatch: 'full',
    component: LpDocumentsTabComponent,
    data: {preventScroll: true}
  },
  {
    path: 'leverage',
    pathMatch: 'full',
    component: LpLeverageComponent,
    data: {preventScroll: true}
  },
  {
    path: 'capital-call',
    pathMatch: 'full',
    component: LpHoldingCapitalCallTabComponent,
    data: {preventScroll: true}
  },
  {
    path: '',
    redirectTo: defaultHoldingTab,
    pathMatch: 'full'
  },
];

const routes = RouterModule.forChild([
  {
    path: 'lp/login',
    component: LoginLpComponent,
    canActivate: [MaintenanceGuard, SubdomainLpGuard, AlreadyLoggedInGuard]
  },
  {
    path: 'lp/signup',
    component: RegisterLpComponent,
    canActivate: [MaintenanceGuard, SubdomainLpGuard]
  },
  {
    path: 'lp/opportunity',
    loadChildren: () => import('./investment-opportunity/investment-opportunity.module').then(m => m.InvestmentOpportunityModule),
    canActivate: [MaintenanceGuard, SubdomainLpGuard, AnonymousPageGuard]
  },
  {
    path: 'lp',
    component: LpComponent,
    canActivate: [MaintenanceGuard, SubdomainLpGuard, AuthorizedLpGuard],
    children: [
      {
        path: 'onboarding',
        component: OnboardingLpComponent,
        canActivate: [LpOnboardingPassedGuard],
        children: [
          {
            path: 'personal',
            component: OnboardingLpPersonalDetailsComponent,
          },
          {
            path: 'organization',
            component: OnboardingLpOrganizationDetailsComponent,
          },
          {
            path: '',
            redirectTo: 'personal',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'portfolio/example',
        pathMatch: 'full',
        redirectTo: '/lp/holdings/a/-999',
      },
      {
        path: 'holdings/:holdingType/:holdingId',
        children: [
          {
            path: '', redirectTo: (route: ActivatedRouteSnapshot) => {
              const holdingId = route.params['holdingId'];
              const holdingType = route.params['holdingType'];
              const queryParams = Object.keys(route.queryParams).map(k => `${k}=${encodeURIComponent(route.queryParams[k])}`).join('&');
              const queryParamsUriFormat = queryParams ? `?${queryParams}` : ''
              return `/lp/portfolio/${holdingType}/${holdingId}/${defaultHoldingTab}${queryParamsUriFormat}`;
            }, pathMatch: 'full'
          },
          {
            path: '**', redirectTo: (route: ActivatedRouteSnapshot) => {
              const holdingId = route.params['holdingId'];
              const holdingType = route.params['holdingType']
              const remainingPath = route.url.join('/');
              const queryParams = Object.keys(route.queryParams).map(k => `${k}=${encodeURIComponent(route.queryParams[k])}`).join('&');
              const queryParamsUriFormat = queryParams ? `?${queryParams}` : ''
              return `/lp/portfolio/${holdingType}/${holdingId}/${remainingPath}${queryParamsUriFormat}`;
            }, pathMatch: 'full'
          }
        ]
      },

      // asset document
      {
        path: 'portfolio/a/:holdingId/documents/:storageObjectId',
        component: FilePreviewPageComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        pathMatch: 'full',
        data: {
          holdingDiscriminator: HoldingDiscriminator.Asset
        }
      },

      // fund document
      {
        path: 'portfolio/f/:holdingId/documents/:storageObjectId',
        component: FilePreviewPageComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        pathMatch: 'full',
        data: {
          holdingDiscriminator: HoldingDiscriminator.Fund
        }
      },
      // Asset routing
      {
        path: 'portfolio/a/:holdingId',
        component: LpAssetComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        children: holdingPageChildren('Asset')
      },
      // Fund routing
      {
        path: 'portfolio/f/:holdingId',
        component: LpFundComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        children: holdingPageChildren('Fund')
      },
      // opportunity asset routing
      {
        path: 'investment-opportunities/a/:holdingId',
        component: LpAssetComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        children: holdingPageChildren('Asset')
      },
      // opportunity fund routing
      {
        path: 'investment-opportunities/f/:holdingId',
        component: LpFundComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard],
        children: holdingPageChildren('Fund')
      },
      {
        path: 'profile',
        component: LpProfileComponent,
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard, LpViewAsInvestorGuard]
      },
      {
        path: 'portfolio',
        loadChildren: () => import('./lp-overview/lp-overview.module').then(m => m.LpOverviewModule),
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard]
      },
      {
        path: 'bank-accounts',
        loadChildren: () => import('./lp-bank-accounts/lp-bank-accounts.module').then(m => m.LpBankAccountsModule),
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard, LpViewAsInvestorGuard],
        data: {helpSourceType: HelpSourceType.LpBanking},
      },
      {
        path: 'documents',
        loadComponent: () => import('./lp-documents/lp-documents.component').then(m => m.LpDocumentsComponent),
        canActivate: [LpOnboardingRequiredGuard, VerifyEmailGuard, LpViewAsInvestorGuard],
        data: {helpSourceType: HelpSourceType.LpDocument},
      },
      {
        path: 'documents/:storageObjectId',
        component: FilePreviewPageComponent,
        pathMatch: 'full'
      },
      {
        path: 'overview',
        redirectTo: '/lp/portfolio',
        pathMatch: 'full'
      },
      {
        path: '',
        redirectTo: '/lp/portfolio',
        pathMatch: 'full'
      }
    ]
  },
  {
    // arriving here from email link
    path: 'lp/sign-request/:investmentId/:uniqueIdentifier',
    component: SignRequestComponent,
    canActivate: [AnonymousPageGuard]
  },
  {
    // arriving here from email link
    path: 'lp/contact/:contactId/subscription/:uniqueIdentifier',
    component: ContactUnsubscribeComponent,
    canActivate: [AnonymousPageGuard]
  }
]);

@NgModule({
  declarations: [
    LpComponent,
    LpAssetsComponent,
    LpAssetComponent,
    LenderInfoDialogComponent,
    LenderJuniorInfoDialogComponent,
    InfoDialogComponent,
    LpProfileComponent,
    PersonalDetailsLpComponent,
    CompanyProfileLpComponent,
    ReportFaqsDialogComponent,
    OnboardingLpComponent,
    OnboardingLpPersonalDetailsComponent,
    OnboardingLpOrganizationDetailsComponent,
    LpTopNotificationComponent,
    AddYourAssetCardComponent,
    GpLeadDialogComponent,
    BulletBigValueComponent,
    AssetSpendToDateSectionComponent,
    AssetDetailsSectionComponent,
    AssetDetailsDialogComponent,
    LpDocumentsTabComponent,
    LpAssetTransactionsTabComponent,
    FileOptionsMenuComponent,
    SelectAndAgreeComponent,
    LpHoldingsComponent,
    LpFundsComponent,
    LpFundComponent,
    LpFundTransactionsTabComponent,
    LenderJuniorInfoDialogComponent,
    HoldingMyPositionSectionComponent,
    LpTransactionDetailsDialogComponent,
    HoldingPositionChartComponent,
    HoldingGeneralInformationComponent,
    LpHoldingTabsNavComponent,
    LpHoldingInformationTabComponent,
    HoldingInvestmentPerformanceComponent,
    HoldingCapitalInformationComponent,
    HoldingProjectInformationComponent,
    HoldingReportsComponent,
    HoldingReportComponent,
    LpHoldingsCardsComponent,
    LpLeverageComponent,
    LpLeverageGraphComponent,
    LpLeverageTableComponent,
    LpHoldingCapitalCallTabComponent,
    LpCapitalCallDetailsComponent,
    LpCapitalCallDetailsLineItemComponent,
    LpHoldingCardComponent,
    PrivateHoldingNotificationComponent,
    InvestingEntityShareComponent,
    InvestingEntityChipComponent,
    InvestingEntityMenuComponent,
    LpAssetFilterComponent,
    LpAssetMobileFilterComponent,
  ],
  exports: [
    LpComponent,
    AssetSpendToDateSectionComponent,
    AssetDetailsSectionComponent,
    HoldingMyPositionSectionComponent,
    BulletBigValueComponent,
    HoldingPositionChartComponent,
    LpHoldingsComponent,
    HoldingReportComponent,
    HoldingInvestmentPerformanceComponent,
    HoldingCapitalInformationComponent,
    HoldingProjectInformationComponent,
    HoldingGeneralInformationComponent,
    LpHoldingsCardsComponent,
    LpHoldingCardComponent,


    // TO Shared?
    LpAssetFilterComponent,
    LpAssetMobileFilterComponent,
  ],
  imports: [
    CommonModule,
    SharedModule,
    CovercySharedComponentsModule,
    MaterialModule,
    AccountModule,
    RouterModule,
    routes,
    NgApexchartsModule,
    AccountLpModule
  ],
  providers: [
    // Guards
    PendingChangesGuard,
    AuthorizedGpGuard,
    AuthorizedLpGuard,
    LpOnboardingRequiredGuard,
    MaintenanceGuard,
    // Services
    LpUserDataService,
    LpHoldingDataService,
    LpAssetDataService,
    LpFundDataService,
    AssetReportDataService,
    FundReportDataService,
    LpBankAccountDataService,
    {provide: USER_DETAILS, useClass: UserService},
    {provide: APP_SETTINGS, useClass: AppSettingsService},
    LpUnitBankApplicationDataService,
    LpUnitBankAccountDataService,
    {provide: UnitBankAccountDataService, useExisting: LpUnitBankAccountDataService},
    {provide: UnitBankApplicationDataService, useExisting: LpUnitBankApplicationDataService},
    UnitCardDataService,
    InvestingEntityShareService
    // {provide: 'UnitCardBaseEndpoint', useValue: of('lp/unit-card')}
  ]
})
export class LpModule {
}
